import { useCallback, useId, useState } from 'react'

import { Composer } from '../../components/assistant/Composer'
import { HowItWorks, HowItWorksDialog } from '../../components/assistant/HowItWorks'
import { Thread } from '../../components/assistant/Thread'
import { PageHeader } from '../../components/PageHeader'
import { ConfirmDialog } from '../../components/ui'
import { useAssistant } from '../../contexts/AssistantContext'
import { deleteSession, listSessions, type ChatSession } from '../../lib/assistant'
import { ABSENT_COPY } from '../../lib/assistantCopy'
import { BREAKPOINTS } from '../../lib/breakpoints'
import { ProblemError } from '../../lib/problem'
import { useMediaQuery } from '../../lib/useMediaQuery'
import { useResource } from '../../lib/useResource'

/**
 * `/assistant` (agent-1-web W5): the assistant's own page, for longer
 * threads. Three columns from 1024 px, as the canvas's "/assistant · full page"
 * draws them -- Recent, the conversation, and "How the assistant works" -- and
 * on a phone the first two stacked, with "How it works" a sheet behind a link.
 *
 * Under `pages/assistant/`, NOT `pages/product/`: that folder is in
 * `clientScience.test.ts`'s every-comparison scope, and this page reads no
 * reading at all -- the conversation is the server's words, rendered by the
 * same components as the drawer.
 *
 * It is the same one conversation the drawer holds (the provider's), so a
 * thread started from a card continues here, and "New conversation" starts one
 * whose context is this page.
 */
export function AssistantPage() {
  const assistant = useAssistant()
  const isDesktop = useMediaQuery(`(min-width: ${BREAKPOINTS.lg}px)`)
  const [older, setOlder] = useState<{ items: ChatSession[]; cursor: string | null } | null>(null)
  const [olderFailed, setOlderFailed] = useState(false)
  const [removed, setRemoved] = useState<ReadonlySet<string>>(new Set())
  const [deleting, setDeleting] = useState<ChatSession | null>(null)
  const [busy, setBusy] = useState(false)
  const [deleteFailed, setDeleteFailed] = useState(false)
  const [howOpen, setHowOpen] = useState(false)
  const recentId = useId()

  // Read ONCE. A conversation started here (or in the drawer) joins the list
  // locally, below -- a re-read would drop the list to nothing and take the
  // keyboard focus with it (W4/W5 review).
  const activeId = (assistant?.thread.loading ?? assistant?.thread.session?.id) ?? null
  const loadFirst = useCallback((signal: AbortSignal) => listSessions({}, signal), [])
  // Every conversation this page has held, newest first: started here, then
  // left for another, it stays listed (final review). Adjusted during render,
  // and again when the server renames it after its first reply (session-titles):
  // the row keeps its place, and the list is not read again.
  const [seen, setSeen] = useState<ChatSession[]>([])
  const active = assistant?.thread.session ?? null
  if (active && (seen[0]?.id !== active.id || seen[0].title !== active.title)) {
    setSeen([active, ...seen.filter((x) => x.id !== active.id)])
  }
  const first = useResource(loadFirst)

  async function showOlder(cursor: string) {
    try {
      const page = await listSessions({ cursor })
      setOlder((prev) => ({ items: [...(prev?.items ?? []), ...page.items], cursor: page.next_cursor }))
      setOlderFailed(false)
    } catch {
      setOlderFailed(true)
    }
  }

  if (!assistant) return null

  async function confirmDelete() {
    if (!deleting || !assistant) return
    setBusy(true)
    try {
      await deleteSession(deleting.id)
      assistant.forget(deleting.id)
      setRemoved((prev) => new Set([...prev, deleting.id]))
      setDeleteFailed(false)
      // The button that opened the dialog is gone with its row: focus goes to
      // the list's heading rather than to nothing (W4/W5 review).
      requestAnimationFrame(() => document.getElementById(recentId)?.focus())
    } catch {
      setDeleteFailed(true)
    } finally {
      setBusy(false)
      setDeleting(null)
    }
  }

  const firstError = first.error
  const absent = firstError instanceof ProblemError && firstError.status === 404 && firstError.code === 'not_found'
  const failed = (firstError !== null && !absent) || olderFailed || deleteFailed
  const unavailable = absent || assistant.absent
  const sessions =
    first.data === null
      ? null
      : [...seen, ...first.data.items, ...(older?.items ?? [])].filter(
          (s, i, all) => !removed.has(s.id) && all.findIndex((t) => t.id === s.id) === i,
        )
  const nextCursor = older ? older.cursor : (first.data?.next_cursor ?? null)

  return (
    <div className="stack stack--wide assistant-page">
      <PageHeader
        eyebrow="Help · Assistant"
        title="Assistant"
        actions={
          !unavailable && (
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              disabled={assistant.thread.turn !== null || assistant.thread.messages.length === 0}
              onClick={assistant.newConversation}
            >
              New conversation
            </button>
          )
        }
      />

      {unavailable ? (
        <div className="card assistant-notice assistant-notice--absent">
          <p>{ABSENT_COPY}</p>
        </div>
      ) : (
        <div className="assistant-page__grid">
          <section className="card assistant-page__recent" aria-labelledby={recentId}>
            <h2 className="eyebrow assistant-page__recent-title" id={recentId} tabIndex={-1}>
              Recent
            </h2>
            {sessions === null && !failed && <p className="assistant-page__note">Loading your conversations…</p>}
            {failed && (
              <div className="assistant-page__note">
                <p>Something went wrong with this list.</p>
                {firstError !== null && (
                  <button type="button" className="btn btn--ghost btn--sm" onClick={first.reload}>
                    Try again
                  </button>
                )}
              </div>
            )}
            {sessions !== null && sessions.length === 0 && <p className="assistant-page__note">No conversations yet.</p>}
            {sessions !== null && sessions.length !== 0 && (
              <ul className="assistant-page__list">
                {sessions.map((s) => (
                  <li key={s.id} className={`assistant-page__item${s.id === activeId ? ' assistant-page__item--active' : ''}`}>
                    <button
                      type="button"
                      className="assistant-page__open"
                      aria-current={s.id === activeId ? 'true' : undefined}
                      onClick={() => void assistant.loadSession(s.id)}
                    >
                      {s.title}
                    </button>
                    <button
                      type="button"
                      className="btn btn--icon assistant-page__delete"
                      aria-label={`Delete the conversation ${s.title}`}
                      onClick={() => setDeleting(s)}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path
                          d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {nextCursor && (
              <button type="button" className="btn btn--ghost btn--sm" onClick={() => void showOlder(nextCursor)}>
                Show older
              </button>
            )}
            <p className="assistant-page__note">
              Conversations are yours. You can delete any of them at any time.
            </p>
          </section>

          <section className="card assistant-page__conversation" aria-label="Conversation">
            {assistant.thread.session && (
              <p className="assistant-chip-context">Started from · {assistant.thread.session.title}</p>
            )}
            <Thread className="assistant-page__thread" starters="empty" />
            <Composer placeholder="Ask about a screen, a measure, or your latest reading…" />
            {!isDesktop && (
              <p className="assistant-page__how-link">
                <button type="button" className="link-button" onClick={() => setHowOpen(true)}>
                  How the assistant works ›
                </button>
              </p>
            )}
          </section>

          {isDesktop && (
            <aside className="assistant-page__how" aria-label="How the assistant works">
              <HowItWorks />
            </aside>
          )}
        </div>
      )}

      {!isDesktop && <HowItWorksDialog open={howOpen} onClose={() => setHowOpen(false)} />}

      <ConfirmDialog
        open={deleting !== null}
        destructive
        busy={busy}
        title="Delete this conversation?"
        consequence={`"${deleting?.title ?? ''}" is removed from your conversations and can't be opened again.`}
        confirmLabel="Delete conversation"
        onConfirm={() => void confirmDelete()}
        onCancel={() => setDeleting(null)}
      />
    </div>
  )
}
